// lib/chart-utils.ts
import { GraphData, ChartType } from "@/types/Artifact";

// Helper function to generate chart colors
export const generateChartColors = (count: number): string[] => {
  const defaultColors = [
    "#3b82f6", "#ef4444", "#10b981", "#f59e0b", "#8b5cf6",
    "#ec4899", "#06b6d4", "#f97316", "#6366f1", "#84cc16",
    "#2563eb", "#dc2626", "#059669", "#d97706", "#7c3aed",
    "#db2777", "#0891b2", "#ea580c", "#4f46e5", "#65a30d",
    "#1d4ed8", "#b91c1c", "#047857", "#b45309", "#6d28d9",
    "#be185d", "#0e7490", "#c2410c", "#4338ca", "#4d7c0f",
    "#60a5fa", "#f87171", "#34d399", "#fbbf24", "#a78bfa",
    "#f472b6", "#22d3ee", "#fb923c", "#818cf8", "#a3e635",
  ];

  return Array(count)
    .fill(0)
    .map((_, i) => defaultColors[i % defaultColors.length]);
};

// Parse graph data from artifact content
export const parseGraphData = (content: any): GraphData | null => {
  if (!content) return null;

  // Handle string content (attempt to parse JSON)
  if (typeof content === "string") {
    try {
      content = JSON.parse(content);
    } catch (e) {
      console.error("Failed to parse graph data", e);
      return null;
    }
  }

  // Handle legacy format where visualizationType is used instead of type
  if (content.visualizationType && !content.type) {
    content.type = content.visualizationType;
    delete content.visualizationType;
  }

  // Validate graph data structure
  if (!content.type || !Array.isArray(content.data)) {
    return null;
  }

  return content as GraphData;
};

// Create chart configuration
export const createChartConfig = (
  graphData: GraphData,
  data: Record<string, any>[]
): { config: Record<string, any>; visualizationKeys: string[]; colors: string[] } | null => {
  if (!data || data.length === 0) return null;

  const firstItem = data[0];
  const xAxisKey = graphData.xAxis;
  const categoryKey = graphData.categoryKey || "category";
  const chartType = graphData.type;

  // Generate config based on chart type
  const config: Record<string, any> = {};
  let visualizationKeys: string[] = [];
  let colorCount = 0;

  switch (chartType) {
    case "pie":
    case "donut":
    case "funnel":
    case "polarArea": {
      // For category-based charts, config maps category values to labels
      data.forEach((item, index) => {
        const categoryValue = item[categoryKey];
        if (categoryValue) {
          config[categoryValue] = {
            label: categoryValue,
            color: `hsl(var(--chart-${index + 1}, 220 70% 50%))`,
          };
        }
      });
      visualizationKeys = []; // These charts don't use visualization keys
      colorCount = data.length;
      break;
    }

    case "radar": {
      // For radar charts, config maps metric keys to labels
      const radarKeys = Object.keys(firstItem).filter((key) => key !== categoryKey);
      radarKeys.forEach((key, index) => {
        config[key] = {
          label: key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1').trim(),
          color: `hsl(var(--chart-${index + 1}, 220 70% 50%))`,
        };
      });
      visualizationKeys = radarKeys;
      colorCount = radarKeys.length;
      break;
    }

    case "scatter":
    case "bubble": {
      // For scatter/bubble charts, config maps series name to label
      const xAxisKey = graphData.xAxis;
      const yAxisKey = graphData.yAxis;
      const seriesName = `${xAxisKey} vs ${yAxisKey}`;
      config[seriesName] = {
        label: seriesName,
        color: `hsl(var(--chart-1, 220 70% 50%))`,
      };
      visualizationKeys = []; // These charts don't use traditional visualization keys
      colorCount = 1;
      break;
    }

    case "line":
    case "bar":
    case "stackedBar":
    case "groupedBar":
    case "area":
    default: {
      // For data series charts, config maps data keys to labels
      visualizationKeys = Object.keys(firstItem).filter((key) => {
        return key !== xAxisKey && key !== categoryKey;
      });
      
      visualizationKeys.forEach((key, index) => {
        config[key] = {
          label: key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1').trim(),
          color: `hsl(var(--chart-${index + 1}, 220 70% 50%))`,
        };
      });
      colorCount = visualizationKeys.length;
      break;
    }
  }

  // Generate colors
  const colors = generateChartColors(colorCount);

  return { config, visualizationKeys, colors };
};

// Validate chart type
export const isValidChartType = (type: string): type is ChartType => {
  const validTypes: ChartType[] = [
    "line", "bar", "area", "pie", "donut", "radar", 
    "scatter", "bubble", "stackedBar", "groupedBar", 
    "funnel", "polarArea"
  ];
  return validTypes.includes(type as ChartType);
};